import { SearchService } from "@/client"
import { AppEmptyState } from "@/components/Common/AppEmptyState"
import { PageShell, PageSkeleton } from "@/components/Layout"
import type { SearchKind } from "@/components/Search/SearchBar"
import { SearchBar } from "@/components/Search/SearchBar"
import { SearchResultCard } from "@/components/Search/SearchResultCard"
import {
  PaginationItems,
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot,
} from "@/components/ui/pagination"
import { useFlashcardsEnabled } from "@/hooks/useFlashcardsEnabled"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"
import { Flex, IconButton, Stack, Text } from "@chakra-ui/react"
import { keepPreviousData, useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useCallback } from "react"
import { FaChevronLeft, FaChevronRight } from "react-icons/fa"
import { FiSearch } from "react-icons/fi"
import { z } from "zod"

const PAGE_SIZE = 20

const searchSchema = z.object({
  q: z.string().optional().catch(undefined),
  topics: z.array(z.string()).optional().catch(undefined),
  subjects: z.array(z.string()).optional().catch(undefined),
  kind: z.enum(["all", "question", "flashcard"]).optional().catch(undefined),
  page: z.number().catch(1),
})

type SearchParams = z.infer<typeof searchSchema>

export const Route = createFileRoute("/_layout/search")({
  component: SearchPage,
  validateSearch: (search) => searchSchema.parse(search),
})

export function SearchPage() {
  const navigate = useNavigate({ from: Route.fullPath })
  const { q, topics, subjects, kind, page } = Route.useSearch()
  const breadcrumbs = useRouteBreadcrumbs("dashboard.root", [
    { label: "Search", isCurrent: true },
  ])
  const { enabled: flashcardsEnabled } = useFlashcardsEnabled()

  const topicIds = topics ?? []
  const subjectIds = subjects ?? []
  // A bookmarked `kind=flashcard` can outlive the org's flashcards setting.
  // The backend would return nothing for it, so fall back to the merged view
  // rather than rendering a filter the user cannot see or clear.
  const activeKind: SearchKind =
    kind === "flashcard" && !flashcardsEnabled ? "all" : kind ?? "all"
  // "All" means "send no `kinds` at all" — an empty array is a different thing
  // downstream, and listing both kinds explicitly would only restate the
  // default.
  const kinds = activeKind === "all" ? undefined : [activeKind]
  const query = (q ?? "").trim()
  // A blank query with no chips matches nothing server-side; there is no reason
  // to ask.
  const hasCriteria =
    query.length > 0 || topicIds.length > 0 || subjectIds.length > 0

  const setSearch = useCallback(
    (next: Partial<SearchParams>) => {
      navigate({
        search: (prev: SearchParams) => ({ ...prev, ...next }),
      })
    },
    [navigate],
  )

  const handleQueryChange = useCallback(
    (value: string) => {
      setSearch({ q: value.trim() ? value : undefined, page: 1 })
    },
    [setSearch],
  )

  const handleTopicIdsChange = useCallback(
    (ids: string[]) => {
      setSearch({ topics: ids.length ? ids : undefined, page: 1 })
    },
    [setSearch],
  )

  const handleSubjectIdsChange = useCallback(
    (ids: string[]) => {
      setSearch({ subjects: ids.length ? ids : undefined, page: 1 })
    },
    [setSearch],
  )

  const handleKindChange = useCallback(
    (nextKind: SearchKind) => {
      // "All" is the default, so it leaves the URL clean rather than pinning
      // an explicit `kind=all` — the same shape the topic/subject params use.
      setSearch({ kind: nextKind === "all" ? undefined : nextKind, page: 1 })
    },
    [setSearch],
  )

  const { data, isLoading } = useQuery({
    queryKey: [
      "question-bank-search",
      { q: query, topicIds, subjectIds, kinds, page },
    ],
    queryFn: () =>
      SearchService.searchQuestionBank({
        q: query || undefined,
        topicIds: topicIds.length ? topicIds : undefined,
        subjectIds: subjectIds.length ? subjectIds : undefined,
        kinds,
        offset: (page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
      }),
    enabled: hasCriteria,
    placeholderData: keepPreviousData,
  })

  const results = data?.data ?? []
  const count = data?.count ?? 0
  // `count` is already scoped to what this caller may see, so it is safe to
  // print. `has_more` says the eligibility window saturated, which makes the
  // count a floor rather than a total — say so instead of stating a number.
  const isApproximate = data?.has_more ?? false

  return (
    <PageShell
      title="Search"
      description="Search the question bank across questions and flashcards."
      breadcrumbs={breadcrumbs}
    >
      <Stack gap={6}>
        <SearchBar
          q={q ?? ""}
          topicIds={topicIds}
          subjectIds={subjectIds}
          kind={activeKind}
          flashcardsEnabled={flashcardsEnabled}
          onQueryChange={handleQueryChange}
          onTopicIdsChange={handleTopicIdsChange}
          onSubjectIdsChange={handleSubjectIdsChange}
          onKindChange={handleKindChange}
        />

        {hasCriteria && data ? (
          <Stack gap={1}>
            <Text color="fg.muted" data-testid="search-result-count">
              {isApproximate
                ? `${count}+ results`
                : `${count} ${count === 1 ? "result" : "results"}`}
            </Text>
            {isApproximate && (
              <Text fontSize="xs" color="fg.muted">
                More matches exist than we could count. Add a topic or subject
                to narrow the search.
              </Text>
            )}
          </Stack>
        ) : null}

        {!hasCriteria ? (
          <AppEmptyState
            title="Start a search"
            description="Search the question bank by keyword, topic or subject"
            icon={<FiSearch />}
          />
        ) : isLoading ? (
          <PageSkeleton variant="table" rows={4} />
        ) : results.length === 0 ? (
          <AppEmptyState
            title="No results found"
            description="Try a different keyword, or remove a topic or subject filter."
            icon={<FiSearch />}
          />
        ) : (
          <Stack gap={4}>
            {results.map((item) => (
              <SearchResultCard key={`${item.kind}-${item.id}`} item={item} />
            ))}
          </Stack>
        )}

        {hasCriteria && count > PAGE_SIZE && (
          <Flex justify="flex-end">
            <PaginationRoot
              count={count}
              pageSize={PAGE_SIZE}
              page={page}
              onPageChange={({ page: nextPage }) =>
                setSearch({ page: nextPage })
              }
            >
              <Flex align="center" gap={2}>
                <PaginationPrevTrigger asChild>
                  <IconButton
                    aria-label="Previous page"
                    size="sm"
                    variant="ghost"
                  >
                    <FaChevronLeft />
                  </IconButton>
                </PaginationPrevTrigger>
                <PaginationItems />
                <PaginationNextTrigger asChild>
                  <IconButton aria-label="Next page" size="sm" variant="ghost">
                    <FaChevronRight />
                  </IconButton>
                </PaginationNextTrigger>
              </Flex>
            </PaginationRoot>
          </Flex>
        )}
      </Stack>
    </PageShell>
  )
}

export default SearchPage
